import React, {useEffect, useState, useContext} from "react"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {Body, H1, H3} from "@irati/ui/src/Typography"
import {getDomain} from "@irati/utils/links"
import authentication from "@irati/auth"
import {WellnessLane, WellnessMedia} from "@irati/types"
import track from "@irati-tracking/platform/platform"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import DrawerContext from "@irati/ui/src/AppContainer/Context/DrawerContext"
import {RouteComponentProps} from "react-router-dom"
import {UTMMedium, UTMSource, addUTMToLink} from "@irati-web/platform/src/utils/shared"
import wellnessService from "@irati/wellness/services/Wellness"
import Flex from "@irati/ui/src/Flex"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Loading from "@irati/ui/src/Loading"
import {IntlCtx} from "@irati/utils/lang/types"
import {hangPromise} from "@irati/utils/promise"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Col, Row} from "@irati/ui/src/Layout"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Spacing from "@irati/ui/src/Spacing"
import {WellnessHandIcon} from "../components/WellnessHandIcon"
import MediaCard from "../components/MediaCard"

type Props = RouteComponentProps<{}, {}>

export const Featured: React.FC<Props> = () => {
  const [load, setLoad] = useState(hangPromise())
  const [featuredLane, setFeaturedLane] = useState<WellnessLane | null>()

  useEffect(() => {
    setLoad(
      wellnessService.getFeaturedLane().then((response) => {
        setFeaturedLane(response)
      })
    )
  }, [])

  return (
    <Loading load={load}>
      {featuredLane && (featuredLane?.mediaList?.length || 0) > 0 ? <FeaturedContent featuredLane={featuredLane} /> : <NoData />}
    </Loading>
  )
}

function NoData() {
  return (
    <Flex direction="column" gap={3} marginTop={3}>
      <Flex alignItems="center" gap={2}>
        <Icon icon={IconTypes.arrowLeft} />
        <ButtonRouterLink className="btn-xs" small to={`/`}>
          <FormattedMessage id="wellness.title" />
        </ButtonRouterLink>
      </Flex>

      <Flex direction="column" margin={"auto"} alignItems={"center"} gap={3}>
        <Flex gap={2} alignItems="center" marginBottom>
          <WellnessHandIcon size="large" />
        </Flex>

        <H1>
          <FormattedMessage id="wellness.feature.noData.title" />
        </H1>

        <H3>
          <FormattedMessage id="wellness.feature.noData.body" />
        </H3>

        <div>
          <ButtonRouterLink primary to={`/`}>
            <FormattedMessage id="wellness.feature.noData.bodyButton" />
          </ButtonRouterLink>
        </div>
      </Flex>
    </Flex>
  )
}

function FeaturedContent({featuredLane}: {featuredLane: WellnessLane}) {
  const intl = useIntl()
  const theme = useTheme()
  const {setDrawerOpen} = useContext(DrawerContext)

  return (
    <Flex direction="column" gap={3} marginTop={3}>
      <Flex alignItems="center" gap={2}>
        <Icon icon={IconTypes.arrowLeft} />
        <ButtonRouterLink className="btn-xs" small to={`/`}>
          <FormattedMessage id="wellness.title" />
        </ButtonRouterLink>
      </Flex>

      <Flex direction="column">
        <Flex gap={2} alignItems="center" marginBottom>
          <WellnessHandIcon />
          <H3>{featuredLane?.title}</H3>
        </Flex>
        <Body maxWidth="none" color={theme.colors.greys.dark}>
          {featuredLane?.description}
        </Body>
      </Flex>

      <Row marginBottom={4}>
        {featuredLane.mediaList?.map((media) => (
          <Col key={media.uuid} paddingLeft={0} paddingRight={0} md={4} sm={6} xs={12}>
            <Spacing marginBottom={4}>
              <MediaCard media={media} onShowAlert={() => handleCopyLink(media, setDrawerOpen, intl)} />
            </Spacing>{" "}
          </Col>
        ))}
      </Row>
    </Flex>
  )
}

const handleCopyLink = (media: WellnessMedia, setDrawerOpen: (drawerData: any) => void, intl: IntlCtx) => {
  const url = addUTMToLink(
    `${getDomain(authentication.business().subdomain)}/wellness/details/${media.uuid}`,
    UTMSource.WELLNESS_CARD,
    UTMMedium.SHARE_LINK
  )

  const drawerData = {
    url,
    title: media.title,
    alertText: "platform.share.alertText",
    source: "wellness",
    type: "card",
    entity_id: media.uuid
  }

  if (navigator?.canShare?.(drawerData)) {
    setDrawerOpen(drawerData)
  } else {
    navigator.clipboard.writeText(url).then(() => {
      track.share({source: "wellness", type: "card", entity_id: media.uuid})
      triggerSuccessToast(intl.formatMessage({id: "wellness.copyVideoLink.title"}))
    })
  }
}
